import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  InformationCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  ArrowsUpDownIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClockIcon,
  UsersIcon,
  ChartBarIcon,
  PlusCircleIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon as CheckCircleSolid,
  ExclamationTriangleIcon as ExclamationTriangleSolid
} from '@heroicons/react/24/solid';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { formatNumber, formatPercentage } from '../../../utils/formatting';
import useValidations from '../../../hooks/useValidations';
import DbtMetadataCard from '../../../components/dbt/DbtMetadataCard';
import { useBilling } from '../../../contexts/BillingContext';

const TableOverview = ({
  columns = [],
  profiling = null,  // Tiered metadata system - single source of truth
  isLoading,
  error,
  tableName,
  connectionId
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedColumns, setExpandedColumns] = useState({});
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  // Check if profiling (Tier 4) is enabled for this plan
  const { isFeatureEnabled, plan, getUpgradeUrl } = useBilling();
  const enabledTiers = plan?.features?.enabled_tiers || [1, 2];
  const profilingEnabled = enabledTiers.includes(4) || isFeatureEnabled('tier4_enabled');

  // Fetch validation data
  const {
    validations: validationResults,
    loading: validationsLoading,
    metrics: validationMetrics
  } = useValidations(connectionId, tableName);

  // Extract column data - use profiling.columns as primary source (single source of truth)
  // This avoids column name mismatching between useTableColumns and useTableProfiling
  // IMPORTANT: This hook must be called before any early returns
  const enrichedColumns = React.useMemo(() => {
    // If we have profiling columns with data, use them as the primary source
    if (profiling?.columns?.length > 0) {
      return profiling.columns.map(col => {
        const p = col.profiling || {};
        return {
          name: col.name,
          type: col.data_type || col.type || 'unknown',
          nullable: col.is_nullable !== undefined ? col.is_nullable : col.nullable,
          nullPercentage: p.null_percentage || 0,
          distinctCount: p.distinct_count || 0,
          distinctPercentage: p.distinct_percentage || 0,
          hasNumericStats: p.min !== undefined || p.max !== undefined || p.avg !== undefined,
          hasTextStats: p.min_length !== undefined || p.max_length !== undefined,
          hasColumnProfilingData: !!col.profiling,
          numericStats: {
            min: p.min,
            max: p.max,
            avg: p.avg
          },
          textStats: {
            min_length: p.min_length,
            max_length: p.max_length,
            avg_length: p.avg_length
          }
        };
      });
    }

    // Fallback to columns prop if no profiling data (columns without stats)
    return columns.map(column => ({
      ...column,
      nullPercentage: 0,
      distinctCount: 0,
      distinctPercentage: 0,
      hasNumericStats: false,
      hasTextStats: false,
      hasColumnProfilingData: false,
      numericStats: {},
      textStats: {}
    }));
  }, [profiling, columns]);

  // Handle errors
  if (error) {
    // Check if this is a "No profile data available" error (404 with specific message)
    const isNoProfileDataError = error?.response?.status === 404 &&
                                 error?.response?.data?.error === "No profile data available";

    if (isNoProfileDataError) {
      // Show helpful message instead of error for tables with no cached profile data
      const apiMessage = error.response.data.message ||
                        "No profile data has been generated for this table yet. Use the 'Run Data Profiling' button to generate profile data.";

      return (
        <div className="text-center py-10">
          <ChartBarIcon className="mx-auto h-10 w-10 text-secondary-400" />
          <h3 className="mt-2 text-sm font-medium text-secondary-900">No Profile Data Available</h3>
          <p className="mt-1 text-sm text-secondary-500">
            {apiMessage}
          </p>
        </div>
      );
    }

    // For other errors, show the generic error message
    return (
      <div className="text-center py-10">
        <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-danger-400" />
        <h3 className="mt-2 text-sm font-medium text-secondary-900">Error loading table data</h3>
        <p className="mt-1 text-sm text-secondary-500">
          There was an error loading the table information.
        </p>
      </div>
    );
  }

  // Loading state - show loading spinner while we're waiting for API responses
  if (isLoading && !profiling && !error) {
    return (
      <div className="text-center py-16">
        <LoadingSpinner size="lg" className="mx-auto" />
        <p className="mt-4 text-sm text-secondary-500">Loading table overview...</p>
      </div>
    );
  }

  // Extract data from tiered profiling system (single source of truth)
  const rowCount = profiling?.rowCount || 0;
  const columnCount = profiling?.columns?.length || columns.length;

  // Get validation summary
  const validationRules = validationResults || [];
  const passedValidations = validationRules.filter(r => r.last_result === true).length;
  const failedValidations = validationRules.filter(r => r.last_result === false).length;
  const totalValidations = validationRules.length;
  const validationStatus = failedValidations > 0 ? 'error' :
                          passedValidations > 0 ? 'success' : 'neutral';

  // Calculate data health score
  const healthScore = totalValidations > 0 ?
    Math.round((passedValidations / totalValidations) * 100) :
    profiling?.rowCount > 0 ? 85 : 0; // Default good score if we have data

  // Check if we have profiling data from tiered system
  const hasProfilingData = profiling && profiling.isProfiled && profiling.columns?.length > 0;

  // Search and sort functions
  const filteredColumns = enrichedColumns.filter(column =>
    column.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortedColumns = [...filteredColumns].sort((a, b) => {
    let aValue, bValue;
    switch(sortField) {
      case 'name':
        aValue = a.name || '';
        bValue = b.name || '';
        break;
      case 'type':
        aValue = a.type || '';
        bValue = b.type || '';
        break;
      case 'nullPercentage':
        aValue = a.nullPercentage;
        bValue = b.nullPercentage;
        break;
      case 'distinctCount':
        aValue = a.distinctCount;
        bValue = b.distinctCount;
        break;
      default:
        aValue = a[sortField] || '';
        bValue = b[sortField] || '';
    }

    const comparison = typeof aValue === 'string' && typeof bValue === 'string' ?
      aValue.localeCompare(bValue) :
      aValue < bValue ? -1 : aValue > bValue ? 1 : 0;

    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const handleSortClick = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const toggleColumnExpanded = (columnName) => {
    setExpandedColumns(prev => ({
      ...prev,
      [columnName]: !prev[columnName]
    }));
  };

  // Check if we should show the summary cards or wait for more data
  const shouldShowSummaryCards = profiling || (!isLoading && !error);
  
  return (
    <div className="space-y-8">
      {/* Loading indicator for refreshing */}
      {isLoading && (profiling || columns.length > 0) && (
        <div className="bg-primary-50 p-4 rounded-md flex items-center">
          <LoadingSpinner size="sm" className="mr-3" />
          <p className="text-sm text-primary-700">Refreshing table data...</p>
        </div>
      )}

      {/* Profiling Notice - different message based on plan */}
      {!hasProfilingData && columns.length > 0 && !isLoading && (
        profilingEnabled ? (
          // User has profiling enabled but hasn't run it yet
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <ClockIcon className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-yellow-800">
                  Profiling Data Pending
                </h3>
                <p className="mt-1 text-sm text-yellow-700">
                  Column statistics (distinct values, null percentages, min/max values) are not yet available.
                  Use the "Run Data Profiling" button above to collect detailed statistics for this table.
                </p>
              </div>
            </div>
          </div>
        ) : (
          // User's plan doesn't include profiling - show upgrade prompt
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start">
              <LockClosedIcon className="h-5 w-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-amber-800">
                  Data Profiling Available on Starter Plan
                </h3>
                <p className="mt-1 text-sm text-amber-700">
                  Upgrade to access column statistics including distinct values, null percentages, and min/max values.
                </p>
                <Link
                  to={getUpgradeUrl ? getUpgradeUrl() : '/settings/billing'}
                  className="mt-3 inline-flex items-center px-3 py-1.5 bg-amber-600 text-white text-sm font-medium rounded-md hover:bg-amber-700"
                >
                  <LockClosedIcon className="h-4 w-4 mr-1.5" />
                  Upgrade to Unlock Profiling
                </Link>
              </div>
            </div>
          </div>
        )
      )}

      {/* Summary Cards - only show if we have profiling data or are not loading */}
      {shouldShowSummaryCards && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {/* Row Count */}
        <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-blue-100 rounded-md p-3">
                <InformationCircleIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Rows</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatNumber(rowCount)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Column Count */}
        <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-purple-100 rounded-md p-3">
                <InformationCircleIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Columns</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatNumber(columnCount)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Data Health */}
        <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className={`flex-shrink-0 rounded-md p-3 ${
                healthScore >= 80 ? 'bg-green-100' : 
                healthScore >= 60 ? 'bg-yellow-100' : 'bg-red-100'
              }`}>
                {healthScore >= 80 ? (
                  <CheckCircleSolid className="h-6 w-6 text-green-600" />
                ) : (
                  <ExclamationTriangleSolid className="h-6 w-6 text-red-600" />
                )}
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Health Score</dt>
                  <dd className={`text-lg font-medium ${
                    healthScore >= 80 ? 'text-green-600' : 
                    healthScore >= 60 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {healthScore}%
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Validation Summary */}
        <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className={`flex-shrink-0 rounded-md p-3 ${
                validationStatus === 'error' ? 'bg-red-100' : 
                validationStatus === 'success' ? 'bg-green-100' : 'bg-gray-100'
              }`}>
                {validationStatus === 'error' ? (
                  <XCircleIcon className="h-6 w-6 text-red-600" />
                ) : validationStatus === 'success' ? (
                  <CheckCircleIcon className="h-6 w-6 text-green-600" />
                ) : (
                  <InformationCircleIcon className="h-6 w-6 text-gray-600" />
                )}
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Validations</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {validationsLoading ? (
                      'Loading...'
                    ) : totalValidations > 0 ? (
                      `${passedValidations}/${totalValidations}`
                    ) : (
                      'None'
                    )}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* DBT Metadata Card - shows if table is linked to a DBT model */}
      {shouldShowSummaryCards && profiling && (
        <DbtMetadataCard
          connectionId={connectionId}
          tableName={tableName}
          database={profiling?.tableCatalog || 'UNKNOWN'}
          schema={profiling?.tableSchema || 'UNKNOWN'}
        />
      )}

      {/* Data Quality Summary - only show if we have validations */}
      {shouldShowSummaryCards && totalValidations > 0 && (
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Data Quality Summary</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <div className="flex items-center">
                <CheckCircleSolid className="h-5 w-5 text-green-600 mr-2" />
                <div>
                  <div className="text-2xl font-bold text-green-700">{passedValidations}</div>
                  <div className="text-sm text-green-600">Passing</div>
                </div>
              </div>
            </div>
            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
              <div className="flex items-center">
                <XCircleIcon className="h-5 w-5 text-red-600 mr-2" />
                <div>
                  <div className="text-2xl font-bold text-red-700">{failedValidations}</div>
                  <div className="text-sm text-red-600">Failing</div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center">
                <ClockIcon className="h-5 w-5 text-gray-600 mr-2" />
                <div>
                  <div className="text-2xl font-bold text-gray-700">{totalValidations - passedValidations - failedValidations}</div>
                  <div className="text-sm text-gray-600">Pending</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Column Statistics - only show if we have column data */}
      {shouldShowSummaryCards && (
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Column Statistics</h3>
            <div className="flex items-center space-x-4">
              <div className="relative max-w-xs">
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Search columns..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    className="group inline-flex items-center"
                    onClick={() => handleSortClick('name')}
                  >
                    Column
                    <ArrowsUpDownIcon className="ml-2 h-4 w-4 text-gray-400" />
                  </button>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    className="group inline-flex items-center"
                    onClick={() => handleSortClick('type')}
                  >
                    Type
                    <ArrowsUpDownIcon className="ml-2 h-4 w-4 text-gray-400" />
                  </button>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nullable
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    className="group inline-flex items-center"
                    onClick={() => handleSortClick('nullPercentage')}
                  >
                    Non-Null %
                    <ArrowsUpDownIcon className="ml-2 h-4 w-4 text-gray-400" />
                  </button>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    className="group inline-flex items-center"
                    onClick={() => handleSortClick('distinctCount')}
                  >
                    Distinct Values
                    <ArrowsUpDownIcon className="ml-2 h-4 w-4 text-gray-400" />
                  </button>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedColumns.map((column, index) => {
                const isExpanded = expandedColumns[column.name];
                return (
                  <React.Fragment key={column.name}>
                    <tr className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <div className="flex items-center">
                          <button
                            onClick={() => toggleColumnExpanded(column.name)}
                            className="mr-2 p-1 hover:bg-gray-100 rounded"
                          >
                            {isExpanded ? (
                              <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                            ) : (
                              <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                            )}
                          </button>
                          {column.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                          {column.type || 'unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {column.nullable ? (
                          <CheckCircleIcon className="h-5 w-5 text-green-500" />
                        ) : (
                          <XCircleIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {column.hasColumnProfilingData ? (
                          formatPercentage(100 - column.nullPercentage)
                        ) : profilingEnabled ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-200">
                            <ClockIcon className="h-3 w-3 mr-1" />
                            Pending
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {column.hasColumnProfilingData ? (
                          formatNumber(column.distinctCount)
                        ) : profilingEnabled ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-200">
                            <ClockIcon className="h-3 w-3 mr-1" />
                            Pending
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <button className="text-blue-600 hover:text-blue-900 text-xs font-medium">
                          Add Validation
                        </button>
                      </td>
                    </tr>
                    
                    {/* Expanded details */}
                    {isExpanded && (
                      <tr className="bg-gray-50">
                        <td colSpan={6} className="px-6 py-4">
                          <div className="bg-white p-4 rounded-md shadow-sm">
                            <h4 className="text-sm font-medium text-gray-900 mb-3">
                              Detailed Statistics: {column.name}
                            </h4>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                              {/* Basic Info */}
                              <div className="bg-gray-50 p-3 rounded-md">
                                <h5 className="text-xs font-medium text-gray-500 uppercase mb-2">
                                  Basic Information
                                </h5>
                                <div className="space-y-1">
                                  <div className="flex justify-between">
                                    <span className="text-sm text-gray-500">Data Type:</span>
                                    <span className="text-sm font-medium text-gray-900">{column.type}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-sm text-gray-500">Nullable:</span>
                                    <span className="text-sm font-medium text-gray-900">
                                      {column.nullable ? 'Yes' : 'No'}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-sm text-gray-500">Distinct %:</span>
                                    {column.hasColumnProfilingData ? (
                                      <span className="text-sm font-medium text-gray-900">
                                        {formatPercentage(column.distinctPercentage)}
                                      </span>
                                    ) : profilingEnabled ? (
                                      <span className="inline-flex items-center text-xs text-yellow-700">
                                        <ClockIcon className="h-3 w-3 mr-1" />
                                        Pending
                                      </span>
                                    ) : (
                                      <span className="text-sm text-gray-400">—</span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Numeric Stats */}
                              {column.hasNumericStats && (
                                <div className="bg-blue-50 p-3 rounded-md">
                                  <h5 className="text-xs font-medium text-blue-700 uppercase mb-2">
                                    Numeric Statistics
                                  </h5>
                                  <div className="space-y-1">
                                    {column.numericStats.min !== undefined && (
                                      <div className="flex justify-between">
                                        <span className="text-sm text-blue-600">Min:</span>
                                        <span className="text-sm font-medium text-blue-900">
                                          {formatNumber(column.numericStats.min)}
                                        </span>
                                      </div>
                                    )}
                                    {column.numericStats.max !== undefined && (
                                      <div className="flex justify-between">
                                        <span className="text-sm text-blue-600">Max:</span>
                                        <span className="text-sm font-medium text-blue-900">
                                          {formatNumber(column.numericStats.max)}
                                        </span>
                                      </div>
                                    )}
                                    {column.numericStats.avg && (
                                      <div className="flex justify-between">
                                        <span className="text-sm text-blue-600">Average:</span>
                                        <span className="text-sm font-medium text-blue-900">
                                          {parseFloat(column.numericStats.avg).toFixed(2)}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Text Stats */}
                              {column.hasTextStats && (
                                <div className="bg-green-50 p-3 rounded-md">
                                  <h5 className="text-xs font-medium text-green-700 uppercase mb-2">
                                    Text Statistics
                                  </h5>
                                  <div className="space-y-1">
                                    <div className="flex justify-between">
                                      <span className="text-sm text-green-600">Min Length:</span>
                                      <span className="text-sm font-medium text-green-900">
                                        {column.textStats.min_length}
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-sm text-green-600">Max Length:</span>
                                      <span className="text-sm font-medium text-green-900">
                                        {column.textStats.max_length}
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-sm text-green-600">Avg Length:</span>
                                      <span className="text-sm font-medium text-green-900">
                                        {parseFloat(column.textStats.avg_length).toFixed(1)}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  );
};

export default TableOverview;